/**
 * Lesson player: loads content and plays it chapter by chapter (Слова → Текст → Повтор →
 * Задания). Progress is saved after every step; finishing a chapter shows its summary.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Lesson } from '../../core/content.ts';
import { langCode } from '../../core/content.ts';
import {
  accuracy,
  buildPlan,
  completeStep,
  completedState,
  emptyStepsState,
  isChapterComplete,
  isLessonComplete,
  nextPosition,
  restartLesson,
  type PlannedStep,
  type StepsState,
} from '../../core/lesson-plan.ts';
import { xpForExercise } from '../../core/progress.ts';
import { exerciseAward } from '../../core/xp-award.ts';
import { useAppData, useLoadedData } from '../../state/app-data.tsx';
import { useTutor } from '../../state/tutor.tsx';
import { useLessonContent } from '../../state/use-lesson.ts';
import { routes, useRouter } from '../../state/router.tsx';
import { useLessonPathStatus } from '../../state/selectors.ts';
import { languageInfo } from '../../ui/course.ts';
import { RoboCat } from '../../ui/RoboCat.tsx';
import { Icon } from '../../ui/Icon.tsx';
import { playFeedback } from '../../ui/feedback.ts';
import { LearnStep } from './LearnStep.tsx';
import { Reader } from './Reader.tsx';
import { GrammarStep } from './GrammarStep.tsx';
import { TaskStep } from './TaskStep.tsx';
import { ExerciseStep } from './ExerciseStep.tsx';
import { QuestionStep } from './QuestionStep.tsx';
import { OpenTaskStep } from './OpenTaskStep.tsx';
import { LessonComplete } from './LessonComplete.tsx';
import './lesson.css';

interface Summary {
  chapterIndex: number;
  lessonDone: boolean;
  accuracy: number;
  xp: number;
  /** Card count when the chapter began; the summary shows how many were added since. */
  cardsBefore: number;
}

function LessonPlayer({ lessonId, lesson }: { lessonId: string; lesson: Lesson }) {
  const { navigate } = useRouter();
  const { progress, cards } = useLoadedData();
  const { saveProgress, addXp, checkAchievements } = useAppData();
  const { setLessonContext } = useTutor();
  const plan = useMemo(() => buildPlan(lesson), [lesson]);
  const [state, setState] = useState<StepsState>(() => {
    const stored = progress[lessonId];
    const steps = stored?.steps ?? emptyStepsState();
    // A finished lesson whose saved steps belong to an older plan still shows as finished.
    return stored?.status === 'done' && steps.done.length === 0 ? completedState(plan) : steps;
  });
  const [summary, setSummary] = useState<Summary | null>(null);
  /** XP and the word-card count when the current chapter began, for its summary. */
  const wordCards = cards.filter((c) => c.kind === 'word').length;
  const chapterStart = useRef({ xp: 0, cards: wordCards });
  const sessionXp = useRef(0);
  const lastAnswer = useRef<boolean | undefined>(undefined);
  const lang = langCode(lesson.targetLang);
  const speechLang = languageInfo(lang).speechLang;

  useEffect(() => {
    setLessonContext({
      lessonTitle: `${lesson.title.text} — «${lesson.title.ru}»`,
      grammar: lesson.grammar.map((g) => g.title),
      words: lesson.words.slice(0, 20).map((w) => w.word),
    });
  }, [lesson, setLessonContext]);

  const position = nextPosition(plan, state);
  const close = () => navigate(routes.home);

  const persist = (next: StepsState) => {
    const previous = progress[lessonId];
    const lessonDone = isLessonComplete(plan, next);
    const done = lessonDone || previous?.status === 'done';
    const share = accuracy(next);
    saveProgress({
      lesson_id: lessonId,
      status: done ? 'done' : 'started',
      steps: next,
      best_accuracy: lessonDone ? Math.max(previous?.best_accuracy ?? 0, share) : previous?.best_accuracy ?? null,
      completed_at: done ? previous?.completed_at ?? new Date().toISOString() : null,
    });
  };

  const completeCurrent = (step: PlannedStep, chapterIndex: number) => {
    const next = completeStep(plan, state, step.key, lastAnswer.current);
    lastAnswer.current = undefined;
    setState(next);
    persist(next);
    const chapter = plan[chapterIndex]!;
    if (isChapterComplete(chapter, next)) {
      playFeedback('complete');
      checkAchievements();
      setSummary({
        chapterIndex,
        lessonDone: isLessonComplete(plan, next),
        accuracy: accuracy(next, chapter.steps.map((s) => s.key)),
        xp: sessionXp.current - chapterStart.current.xp,
        cardsBefore: chapterStart.current.cards,
      });
    }
  };

  const onAnswered = (step: PlannedStep, correct: boolean): number => {
    lastAnswer.current = correct;
    playFeedback(correct ? 'correct' : 'wrong');
    const firstAttempt = !(step.key in state.results);
    const xp = firstAttempt ? xpForExercise(correct) : 0;
    if (xp > 0) {
      sessionXp.current += xp;
      addXp(xp, exerciseAward(lessonId, step.key));
    }
    return xp;
  };

  if (summary) {
    const chapter = plan[summary.chapterIndex]!;
    const nextChapter = nextPosition(plan, state);
    const continueNext = () => {
      chapterStart.current = { xp: sessionXp.current, cards: wordCards };
      setSummary(null);
    };
    return (
      <LessonComplete
        title={summary.lessonDone ? 'Урок пройден!' : 'Глава пройдена!'}
        subtitle={`${lesson.title.text} · ${chapter.name}`}
        xp={summary.xp}
        accuracy={summary.accuracy}
        newWords={Math.max(0, wordCards - summary.cardsBefore)}
        continueLabel={nextChapter ? `Дальше: ${plan[nextChapter.chapterIndex]!.name}` : 'Продолжить'}
        onContinue={nextChapter ? continueNext : close}
      />
    );
  }

  if (!position) {
    return (
      <div className="center-screen">
        <div className="stack stack--24">
          <RoboCat size={120} state="happy" />
          <h1 className="t-screen">Урок {String(lesson.number)} пройден</h1>
          <p className="han">{lesson.title.text}</p>
          <button type="button" className="btn btn--secondary" onClick={() => setState(restartLesson(state))}>
            Пройти ещё раз
          </button>
          <button type="button" className="btn btn--primary" onClick={close}>
            Продолжить
          </button>
        </div>
      </div>
    );
  }

  const chapter = plan[position.chapterIndex]!;
  const step = chapter.steps[position.stepIndex]!;
  const done = () => completeCurrent(step, position.chapterIndex);
  const spec = step.spec;

  return (
    <div className="lesson">
      <header className="lesson__head">
        <button type="button" className="icon-btn" onClick={close} aria-label="Закрыть урок">
          <Icon name="close" size={26} />
        </button>
        <div
          className="progress lesson__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={chapter.steps.length}
          aria-valuenow={position.stepIndex}
          aria-label={`Прогресс главы «${chapter.name}»`}
        >
          <div className="progress__fill" style={{ width: `${(position.stepIndex / chapter.steps.length) * 100}%` }} />
        </div>
      </header>
      <p className="lesson__chapter">
        Глава {chapter.index} · {chapter.name}
        <span className="lesson__chapter-lesson han"> · {lesson.title.text}</span>
      </p>

      <div className="lesson__step" key={step.key}>
        {spec.kind === 'learn' && (
          <LearnStep lesson={lesson} wordIds={spec.wordIds} speechLang={speechLang} onDone={done} />
        )}
        {spec.kind === 'read' && (
          <Reader
            lesson={lesson}
            text={lesson.texts.find((t) => t.id === spec.textId) ?? lesson.texts[0]!}
            {...(spec.sentenceIds ? { sentenceIds: spec.sentenceIds } : {})}
            speechLang={speechLang}
            onDone={done}
          />
        )}
        {spec.kind === 'question' && (
          <QuestionStep
            lesson={lesson}
            lessonId={lessonId}
            questionId={spec.id}
            textId={spec.textId}
            sentenceIds={spec.sentenceIds}
            fallback={spec.fallback}
            speechLang={speechLang}
            onAnswered={(correct) => onAnswered(step, correct)}
            onDone={done}
          />
        )}
        {spec.kind === 'open' && (
          <OpenTaskStep
            lesson={lesson}
            task={spec.task}
            speechLang={speechLang}
            onAnswered={(correct) => onAnswered(step, correct)}
            onDone={done}
          />
        )}
        {spec.kind === 'grammar' && (
          <GrammarStep lesson={lesson} grammarIds={spec.grammarIds} speechLang={speechLang} onDone={done} />
        )}
        {spec.kind === 'task' && <TaskStep lesson={lesson} taskId={spec.taskId} onDone={done} />}
        {spec.kind === 'exercise' && (
          <ExerciseStep
            lesson={lesson}
            exercise={spec.exercise}
            speechLang={speechLang}
            onAnswered={(correct) => onAnswered(step, correct)}
            onDone={done}
          />
        )}
      </div>
    </div>
  );
}

export function LessonScreen({ lessonId }: { lessonId: string }) {
  const { navigate } = useRouter();
  const { lessons } = useLoadedData();
  const meta = lessons.find((l) => l.id === lessonId) ?? null;
  const pathStatus = useLessonPathStatus(lessonId);
  const comingSoon = meta !== null && !meta.available;
  // Lessons open strictly in path order: a later lesson waits until the previous one is done.
  const locked = !comingSoon && pathStatus === 'locked';
  const loaded = useLessonContent(comingSoon || locked ? null : lessonId, meta?.revision ?? null);

  if (locked) {
    return (
      <div className="center-screen">
        <div className="stack">
          <RoboCat size={80} state="thinking" />
          <p>Сначала пройди предыдущий урок — уроки открываются по порядку.</p>
          <button type="button" className="btn btn--secondary" onClick={() => navigate(routes.home)}>
            На главную
          </button>
        </div>
      </div>
    );
  }

  if (comingSoon) {
    return (
      <div className="center-screen">
        <div className="stack">
          <RoboCat size={80} state="thinking" />
          <p>Урок «{meta.title}» скоро появится</p>
          <button type="button" className="btn btn--secondary" onClick={() => navigate(routes.home)}>
            На главную
          </button>
        </div>
      </div>
    );
  }
  if (loaded.status === 'loading') {
    return (
      <div className="center-screen">
        <div className="stack">
          <RoboCat size={80} state="thinking" />
          <p className="t-meta">Открываем урок…</p>
        </div>
      </div>
    );
  }
  if (loaded.status === 'error') {
    return (
      <div className="center-screen">
        <div className="stack">
          <RoboCat size={80} state="sad" />
          <p>{loaded.message}</p>
          <button type="button" className="btn btn--secondary" onClick={() => navigate(routes.home)}>
            На главную
          </button>
        </div>
      </div>
    );
  }
  return <LessonPlayer key={lessonId} lessonId={lessonId} lesson={loaded.content.lesson} />;
}
